// Brief 89 -- BonsaiPenTcs wizard page (client-facing T&Cs generator).
//
// Separate page from BonsaiPen.jsx (which handles SHAs). Posts to
// /api/tcs/generate. The three axes drive module enablement; per-product
// sub-flags drive cooling-off carve-outs; house-toggles enable optional
// modules; choose-one selectors drive M11 / M17 / M18 variants.
//
// LOC budget per Brief 89 design picks: <= 320 LOC for this page.

import React, { useState } from "react";
import { authFetch } from "../config/api";

const PARTY_OPTIONS = [
  { value: "B2C", label: "B2C -- Consumer only" },
  { value: "B2B", label: "B2B -- Business only" },
  { value: "BOTH", label: "BOTH -- Dual-facing with consumer-rights annex" },
];
const SUPPLY_OPTIONS = [
  { value: "GOODS", label: "Goods" },
  { value: "SERVICES", label: "Services" },
  { value: "DIGITAL", label: "Digital content" },
  { value: "MIXED", label: "Mixed (any combination)" },
];
const CHANNEL_OPTIONS = [
  { value: "ONLINE", label: "Online / distance" },
  { value: "OFFLINE", label: "Offline / on-premises" },
  { value: "BOTH", label: "Both online and offline" },
];
const DEPOSIT_VARIANT_OPTIONS = [
  { value: "refundable", label: "Refundable on account" },
  { value: "part_refundable", label: "Part-refundable (X% retained)" },
  { value: "non_refundable", label: "Non-refundable" },
];
const CAP_OPTIONS = [
  { value: "price_fees", label: "Price or fees paid" },
  { value: "project_value", label: "Stated project / contract value" },
  { value: "pi_cover", label: "Professional indemnity cover limit" },
  { value: "fixed_sum", label: "Fixed sum (GBP)" },
];
const IP_OPTIONS = [
  { value: "retain", label: "We retain IP" },
  { value: "licence_on_payment", label: "Licence on full payment" },
  { value: "assign_on_payment", label: "Assignment on full payment" },
];

const DEFAULT_REQ = {
  party_basis: "B2C",
  supply_type: "SERVICES",
  sales_channel: "ONLINE",
  products: [],
  m11_deposits_enabled: false,
  m25_commercial_warranty: false,
  m26_aup_enabled: false,
  m28_subscription_enabled: false,
  m29_disclaimers_enabled: false,
  m29_health_enabled: false,
  m30_confidentiality_enabled: false,
  m31_scheduling_enabled: false,
  m32_insurance_enabled: false,
  m11_deposit_variant: "refundable",
  m17_cap_option: "price_fees",
  m18_ip_variant: "retain",
  trader_name: "",
  trader_company_number: "",
  trader_registered_office: "",
  product_description: "",
  complaints_contact: "",
  jurisdiction_city: "London",
};

const TOGGLES = [
  ["m11_deposits_enabled", "M11 -- Deposits and money on account"],
  ["m25_commercial_warranty", "M25 -- Commercial warranty"],
  ["m26_aup_enabled", "M26 -- Website accounts and AUP"],
  ["m28_subscription_enabled", "M28 -- Subscription / minimum term"],
  ["m29_disclaimers_enabled", "M29 -- Disclaimers"],
  ["m29_health_enabled", "M29.2 -- Health-services sub-toggle (requires M29)"],
  ["m30_confidentiality_enabled", "M30 -- Confidentiality"],
  ["m31_scheduling_enabled", "M31 -- Scheduling, lateness, missed sessions"],
  ["m32_insurance_enabled", "M32 -- Insurance"],
];

const STEPS = [
  { id: 1, label: "Axes" },
  { id: 2, label: "Products" },
  { id: 3, label: "Toggles" },
  { id: 4, label: "Variants" },
  { id: 5, label: "Trader identity" },
  { id: 6, label: "Review" },
];

export default function BonsaiPenTcs({ initialStep } = {}) {
  const [step, setStep] = useState(initialStep ?? 1);
  const [req, setReq] = useState(DEFAULT_REQ);
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setReq((r) => ({ ...r, [k]: v }));

  const addProduct = () =>
    setReq((r) => ({
      ...r,
      products: [
        ...r.products,
        { name: "", made_to_order: false, digital_immediate_supply: false, off_premises: false },
      ],
    }));
  const updateProduct = (idx, key, v) =>
    setReq((r) => ({
      ...r,
      products: r.products.map((p, i) => (i === idx ? { ...p, [key]: v } : p)),
    }));
  const removeProduct = (idx) =>
    setReq((r) => ({ ...r, products: r.products.filter((_, i) => i !== idx) }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // BM-H141 (T&Cs audit, 2026-09-01): must use authFetch so the
      // X-Bonsai-User-Id header AND the bonsai_session cookie ride the
      // POST. Raw fetch() previously worked only because tcs_router was
      // anon-reachable (see backend/server.py comment on tcs_router in
      // _SEALED_ROUTER_NAMES). Now sealed, so a bare fetch would 401.
      const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/tcs/generate";
      const res = await authFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      setOutput(await res.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bonsai-tcs-page" className="mx-auto max-w-3xl p-6">
      <h1 className="text-3xl font-semibold mb-2">Bonsai Pen -- TCs Generator</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Generate trader-facing Terms and Conditions. Output Terms are the
        trader's terms. The supplier in the issued document is the trader,
        never the instructing law firm.
      </p>

      <nav className="flex gap-2 mb-6">
        {STEPS.map((s) => (
          <button
            key={s.id}
            data-testid={"tcs-step-jump-" + s.id}
            className={
              "rounded px-3 py-1 text-xs " +
              (step === s.id ? "bg-black text-white" : "bg-neutral-200")
            }
            onClick={() => setStep(s.id)}
          >
            {s.id}. {s.label}
          </button>
        ))}
      </nav>

      {step === 1 && (
        <section data-testid="tcs-step-1-panel" className="space-y-4">
          <Select testid="tcs-party-basis-select" label="Party basis"
                  value={req.party_basis} onChange={(v) => set("party_basis", v)}
                  options={PARTY_OPTIONS} />
          <Select testid="tcs-supply-type-select" label="Supply type"
                  value={req.supply_type} onChange={(v) => set("supply_type", v)}
                  options={SUPPLY_OPTIONS} />
          <Select testid="tcs-sales-channel-select" label="Sales channel"
                  value={req.sales_channel} onChange={(v) => set("sales_channel", v)}
                  options={CHANNEL_OPTIONS} />
        </section>
      )}

      {step === 2 && (
        <section data-testid="tcs-step-2-panel" className="space-y-3">
          <h2 className="text-lg font-semibold">Products and per-product sub-flags</h2>
          <p className="text-xs text-muted-foreground">
            Each product line carries its own made_to_order /
            digital_immediate_supply / off_premises flags. Carve-outs are
            product-specific (M12.5 and M12.6).
          </p>
          {req.products.map((p, i) => (
            <div key={i} data-testid={"tcs-product-row-" + i} className="rounded border p-3 space-y-2">
              <input
                data-testid={"tcs-product-name-" + i}
                className="w-full rounded border px-2 py-1"
                placeholder="Product name"
                value={p.name}
                onChange={(e) => updateProduct(i, "name", e.target.value)}
              />
              <Checkbox testid={"tcs-product-made-to-order-" + i} label="Made to order"
                        checked={p.made_to_order}
                        onChange={(v) => updateProduct(i, "made_to_order", v)} />
              <Checkbox testid={"tcs-product-digital-immediate-supply-" + i}
                        label="Digital, immediate supply"
                        checked={p.digital_immediate_supply}
                        onChange={(v) => updateProduct(i, "digital_immediate_supply", v)} />
              <Checkbox testid={"tcs-product-off-premises-" + i} label="Off-premises"
                        checked={p.off_premises}
                        onChange={(v) => updateProduct(i, "off_premises", v)} />
              <button data-testid={"tcs-product-remove-" + i}
                      className="text-xs underline text-red-700"
                      onClick={() => removeProduct(i)}>Remove</button>
            </div>
          ))}
          <button data-testid="tcs-product-add" className="rounded bg-neutral-900 text-white px-3 py-1 text-sm"
                  onClick={addProduct}>Add product</button>
        </section>
      )}

      {step === 3 && (
        <section data-testid="tcs-step-3-panel" className="space-y-2">
          <h2 className="text-lg font-semibold">House-toggle modules</h2>
          {TOGGLES.map(([k, label]) => (
            <Checkbox key={k} testid={"tcs-toggle-" + k.replace(/_/g, "-")}
                      label={label} checked={req[k]}
                      onChange={(v) => set(k, v)} />
          ))}
        </section>
      )}

      {step === 4 && (
        <section data-testid="tcs-step-4-panel" className="space-y-4">
          <Select testid="tcs-m11-deposit-variant-select" label="M11 deposit variant"
                  value={req.m11_deposit_variant}
                  onChange={(v) => set("m11_deposit_variant", v)}
                  options={DEPOSIT_VARIANT_OPTIONS} />
          <Select testid="tcs-m17-cap-option-select" label="M17 B2B liability cap"
                  value={req.m17_cap_option}
                  onChange={(v) => set("m17_cap_option", v)}
                  options={CAP_OPTIONS} />
          <Select testid="tcs-m18-ip-variant-select" label="M18 IP allocation"
                  value={req.m18_ip_variant}
                  onChange={(v) => set("m18_ip_variant", v)}
                  options={IP_OPTIONS} />
        </section>
      )}

      {step === 5 && (
        <section data-testid="tcs-step-5-panel" className="space-y-3">
          <TextInput testid="tcs-trader-name-input" label="Trader name"
                     value={req.trader_name} onChange={(v) => set("trader_name", v)} />
          <TextInput testid="tcs-trader-company-number-input" label="Company number"
                     value={req.trader_company_number}
                     onChange={(v) => set("trader_company_number", v)} />
          <TextInput testid="tcs-trader-registered-office-input" label="Registered office"
                     value={req.trader_registered_office}
                     onChange={(v) => set("trader_registered_office", v)} />
          <TextInput testid="tcs-complaints-contact-input" label="Complaints contact (M20.1)"
                     value={req.complaints_contact}
                     onChange={(v) => set("complaints_contact", v)} />
          <TextInput testid="tcs-jurisdiction-city-input" label="Jurisdiction city (M23.3 B2B)"
                     value={req.jurisdiction_city}
                     onChange={(v) => set("jurisdiction_city", v)} />
        </section>
      )}

      {step === 6 && (
        <section data-testid="tcs-step-6-panel" className="space-y-3">
          <h2 className="text-lg font-semibold">Review</h2>
          <ReviewRow testid="tcs-review-party-basis" label="Party basis" value={req.party_basis} />
          <ReviewRow testid="tcs-review-supply-type" label="Supply type" value={req.supply_type} />
          <ReviewRow testid="tcs-review-sales-channel" label="Sales channel" value={req.sales_channel} />
          <ReviewRow testid="tcs-review-deposit-variant" label="M11 deposit" value={req.m11_deposit_variant} />
          <ReviewRow testid="tcs-review-cap-option" label="M17 cap" value={req.m17_cap_option} />
          <ReviewRow testid="tcs-review-ip-variant" label="M18 IP" value={req.m18_ip_variant} />
          <ReviewRow testid="tcs-review-products-count" label="Products" value={String(req.products.length)} />
          <button data-testid="tcs-generate-button"
                  disabled={busy}
                  className="mt-3 rounded bg-black text-white px-4 py-2"
                  onClick={submit}>
            {busy ? "Generating..." : "Generate TCs"}
          </button>
          {error && <p data-testid="tcs-error" className="text-red-700 text-sm">{error}</p>}
          {output && (
            <div data-testid="tcs-output" className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground">
                Active modules: <span data-testid="tcs-active-modules">{output.active_modules.join(", ")}</span>
              </p>
              <textarea readOnly value={output.text}
                        data-testid="tcs-output-text"
                        className="w-full h-80 rounded border p-2 font-mono text-xs" />
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1">
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input data-testid={testid} type="checkbox" checked={checked}
             onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function TextInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input data-testid={testid} className="mt-1 w-full rounded border px-2 py-1"
             value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function ReviewRow({ label, value, testid }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-muted-foreground w-32">{label}:</span>
      <span data-testid={testid} className="font-medium">{value}</span>
    </div>
  );
}
